import { GeometryProjector } from "@shared/mirage/utils/GeometryProjector";
import L from "leaflet";

export const useFeatureGroupToWorkingGeometry = (updateWorkingGeometry: (geoJsonString: string) => void) => {
    const featureGroupToWorkingGeometry = (featureGroup: React.MutableRefObject<L.FeatureGroup<any>>): string | null => {
        const layers: L.Layer[] = [];
        const originalBoundaryLayers: L.Layer[] = [];

        featureGroup.current.eachLayer((layer) => {
            if ((layer as any)._originalBoundary) {
                originalBoundaryLayers.push(layer);
            } else {
                layers.push(layer);
            }
        });

        const tempFeatureGroup = new L.FeatureGroup();
        layers.forEach((layer) => tempFeatureGroup.addLayer(layer));

        const featureCollection = tempFeatureGroup.toGeoJSON() as GeoJSON.FeatureCollection;

        const originalBoundaryFeatures: GeoJSON.Feature[] = [];
        originalBoundaryLayers.forEach((layer) => {
            const layerGeoJSON = (layer as any).toGeoJSON();
            layerGeoJSON.geometry.coordinates = (layer as any)._originalBoundary;
            originalBoundaryFeatures.push(layerGeoJSON);
        });

        if ((!featureCollection.features || featureCollection.features.length === 0) && originalBoundaryFeatures.length === 0) {
            updateWorkingGeometry("");
            return null;
        }

        let projectedRegular: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features: [],
        };

        if (featureCollection.features && featureCollection.features.length > 0) {
            projectedRegular = GeometryProjector.projectTo27700(featureCollection);
        }

        const projected: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features: [...projectedRegular.features, ...originalBoundaryFeatures],
        };

        if (projected.features.length === 0) {
            updateWorkingGeometry("");
            return null;
        }

        if (projected.features.length === 1) {
            const workingGeometry = JSON.stringify(projected.features[0].geometry);
            updateWorkingGeometry(workingGeometry);
            return workingGeometry;
        }

        const pointGeometries: GeoJSON.Point[] = [];
        const multiPointGeometries: GeoJSON.MultiPoint[] = [];

        const lineGeometries: GeoJSON.LineString[] = [];
        const multiLineGeometries: GeoJSON.MultiLineString[] = [];

        const polygonGeometries: GeoJSON.Polygon[] = [];
        const multiPolygonGeometries: GeoJSON.MultiPolygon[] = [];

        const otherGeometries: GeoJSON.Geometry[] = [];

        projected.features.forEach((feature) => {
            const geometry = feature.geometry;

            switch (geometry.type) {
                case "Point":
                    pointGeometries.push(geometry as GeoJSON.Point);
                    break;
                case "MultiPoint":
                    multiPointGeometries.push(geometry as GeoJSON.MultiPoint);
                    break;
                case "LineString":
                    lineGeometries.push(geometry as GeoJSON.LineString);
                    break;
                case "MultiLineString":
                    multiLineGeometries.push(geometry as GeoJSON.MultiLineString);
                    break;
                case "Polygon":
                    polygonGeometries.push(geometry as GeoJSON.Polygon);
                    break;
                case "MultiPolygon":
                    multiPolygonGeometries.push(geometry as GeoJSON.MultiPolygon);
                    break;
                default:
                    otherGeometries.push(geometry);
                    break;
            }
        });

        const resultGeometries: GeoJSON.Geometry[] = [];

        if (pointGeometries.length > 0 || multiPointGeometries.length > 0) {
            const coordinates: number[][] = [];

            pointGeometries.forEach((point) => {
                coordinates.push(point.coordinates);
            });

            multiPointGeometries.forEach((multiPoint) => {
                coordinates.push(...multiPoint.coordinates);
            });

            if (coordinates.length > 0) {
                resultGeometries.push({
                    type: "MultiPoint",
                    coordinates: coordinates,
                });
            }
        }

        if (lineGeometries.length > 0 || multiLineGeometries.length > 0) {
            const coordinates: number[][][] = [];

            lineGeometries.forEach((line) => {
                coordinates.push(line.coordinates);
            });

            multiLineGeometries.forEach((multiLine) => {
                coordinates.push(...multiLine.coordinates);
            });

            if (coordinates.length > 0) {
                resultGeometries.push({
                    type: "MultiLineString",
                    coordinates: coordinates,
                });
            }
        }

        if (polygonGeometries.length > 0 || multiPolygonGeometries.length > 0) {
            const coordinates: number[][][][] = [];

            polygonGeometries.forEach((polygon) => {
                coordinates.push(polygon.coordinates);
            });

            multiPolygonGeometries.forEach((multiPolygon) => {
                coordinates.push(...multiPolygon.coordinates);
            });

            if (coordinates.length > 0) {
                resultGeometries.push({
                    type: "MultiPolygon",
                    coordinates: coordinates,
                });
            }
        }

        if (otherGeometries.length > 0) {
            resultGeometries.push(...otherGeometries);
        }

        if (resultGeometries.length === 0) {
            updateWorkingGeometry("");
            return null;
        } else if (resultGeometries.length === 1) {
            const workingGeometry = JSON.stringify(resultGeometries[0]);
            updateWorkingGeometry(workingGeometry);
            return workingGeometry;
        } else {
            const workingGeometry = JSON.stringify({
                type: "GeometryCollection",
                geometries: resultGeometries,
            });
            updateWorkingGeometry(workingGeometry);
            return workingGeometry;
        }
    };

    return featureGroupToWorkingGeometry;
};
